import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  GameOverlay,
  GameStatus,
  setCanvasSize,
  useFinish,
} from "./gameShared";

const usePauseOnBlur = (setPaused) => {
  useEffect(() => {
    const blur = () => setPaused(true);
    window.addEventListener("blur", blur);
    return () => window.removeEventListener("blur", blur);
  }, [setPaused]);
};

const PauseButton = ({ paused, disabled, setPaused }) => (
  <button
    className="hud-button"
    type="button"
    disabled={disabled}
    onClick={() => setPaused((value) => !value)}
  >
    {paused ? "Continue" : "Pause"}
  </button>
);

export function RunnerGame({ game, unlocked, onFinish }) {
  const canvasRef = useRef(null);
  const frame = useRef(null);
  const model = useRef(null);
  const [status, setStatus] = useState("ready");
  const [paused, setPaused] = useState(false);
  const [distance, setDistance] = useState(0);
  const finish = useFinish(onFinish);
  const goal = unlocked
    ? game.demo.config.fullDistance
    : game.demo.config.distance;
  const setup = useCallback(() => {
    model.current = {
      y: 0,
      vy: 0,
      obstacles: [650, 950],
      distance: 0,
      tick: 0,
    };
    setDistance(0);
    setPaused(false);
    setStatus("playing");
  }, []);
  const jump = useCallback(() => {
    if (status === "playing" && !paused && model.current?.y === 0)
      model.current.vy = 13;
  }, [paused, status]);
  usePauseOnBlur(setPaused);
  useEffect(() => {
    const key = (event) => {
      if ([" ", "ArrowUp", "p"].includes(event.key)) event.preventDefault();
      if (event.key === " " || event.key === "ArrowUp") jump();
      if (event.key.toLowerCase() === "p") setPaused((value) => !value);
    };
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  }, [jump]);
  useEffect(() => {
    if (status !== "playing" || paused) return undefined;
    const canvas = canvasRef.current;
    const context = setCanvasSize(canvas, 640, 360);
    const tick = () => {
      const state = model.current;
      state.tick += 1;
      state.distance += game.demo.config.speed / 4;
      if (state.tick % 6 === 0) setDistance(Math.floor(state.distance));
      state.y += state.vy;
      state.vy -= 0.75;
      if (state.y < 0) {
        state.y = 0;
        state.vy = 0;
      }
      state.obstacles = state.obstacles
        .map((x) => x - game.demo.config.speed)
        .filter((x) => x > -60);
      if (state.obstacles.length < 2)
        state.obstacles.push(
          Math.max(...state.obstacles, 620) + 280 + (state.tick % 120)
        );
      if (state.obstacles.some((x) => x < 152 && x > 98 && state.y < 52)) {
        setStatus("lost");
        finish("lost", Math.floor(state.distance));
        return;
      }
      if (state.distance >= goal) {
        setStatus("won");
        finish("won", Math.floor(state.distance));
        return;
      }
      const sky = context.createLinearGradient(0, 0, 0, 360);
      sky.addColorStop(0, "#cfeeff");
      sky.addColorStop(1, "#fff2d1");
      context.fillStyle = sky;
      context.fillRect(0, 0, 640, 360);
      context.fillStyle = "rgba(255,255,255,.8)";
      for (let i = 0; i < 7; i += 1) {
        const x =
          ((((i * 123 - state.distance * 0.35) % 760) + 760) % 760) - 50;
        context.beginPath();
        context.arc(x, 70 + (i % 3) * 36, 28, 0, Math.PI * 2);
        context.fill();
      }
      context.fillStyle = "#5a9747";
      context.fillRect(0, 300, 640, 60);
      context.fillStyle = "#315b32";
      context.fillRect(0, 315, 640, 45);
      context.fillStyle = "#7c3aed";
      context.beginPath();
      context.arc(120, 263 - state.y, 22, 0, Math.PI * 2);
      context.fill();
      context.fillStyle = "#fff";
      context.fillRect(105, 280 - state.y, 30, 20);
      context.fillStyle = "#8b5a2b";
      state.obstacles.forEach((x) => {
        context.fillRect(x + 15, 258, 12, 42);
        context.fillStyle = "#ef4444";
        context.beginPath();
        context.arc(x + 21, 252, 22, 0, Math.PI * 2);
        context.fill();
        context.fillStyle = "#8b5a2b";
      });
      frame.current = requestAnimationFrame(tick);
    };
    frame.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame.current);
  }, [finish, game.demo.config.speed, goal, paused, status]);
  return (
    <div className="play-surface canvas-game runner-game">
      <div className="game-hud">
        <GameStatus label="Distance" value={`${distance}/${goal}m`} />
        <GameStatus label="Edition" value={unlocked ? "Full" : "Demo"} />
        <PauseButton
          paused={paused}
          disabled={status !== "playing"}
          setPaused={setPaused}
        />
      </div>
      <canvas ref={canvasRef} aria-label="Forest runner play field" />
      <button className="jump-button" onClick={jump}>
        Jump ↑
      </button>
      {status === "ready" && (
        <GameOverlay
          title={`Run ${goal} metres`}
          copy="Jump over roots and thorny shrubs. Space, Arrow Up or tap."
          action="Start run"
          onAction={setup}
        />
      )}
      {paused && status === "playing" && (
        <GameOverlay
          title="Paused"
          copy="Your runner is waiting."
          action="Continue"
          onAction={() => setPaused(false)}
        />
      )}
      {(status === "won" || status === "lost") && (
        <GameOverlay
          title={status === "won" ? "Glade crossed!" : "The thorns caught you"}
          copy={`${distance} metres reached.`}
          action="Run again"
          onAction={setup}
        />
      )}
    </div>
  );
}

export function FlyerGame({ game, unlocked, onFinish }) {
  const canvasRef = useRef(null);
  const frame = useRef(null);
  const model = useRef(null);
  const [status, setStatus] = useState("ready");
  const [paused, setPaused] = useState(false);
  const [gates, setGates] = useState(0);
  const finish = useFinish(onFinish);
  const goal = unlocked ? 16 : 6;
  const setup = useCallback(() => {
    model.current = {
      y: 180,
      vy: 0,
      gates: 0,
      tick: 0,
      pillars: [
        { x: 520, gap: 175, passed: false },
        { x: 820, gap: 120, passed: false },
      ],
    };
    setGates(0);
    setPaused(false);
    setStatus("playing");
  }, []);
  const flap = useCallback(() => {
    if (status === "playing" && !paused && model.current)
      model.current.vy = -6.4;
  }, [paused, status]);
  usePauseOnBlur(setPaused);
  useEffect(() => {
    const key = (event) => {
      if (event.key === " " || event.key === "ArrowUp") {
        event.preventDefault();
        flap();
      }
      if (event.key.toLowerCase() === "p") setPaused((value) => !value);
    };
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  }, [flap]);
  useEffect(() => {
    if (status !== "playing" || paused) return undefined;
    const context = setCanvasSize(canvasRef.current, 640, 360);
    const tick = () => {
      const state = model.current;
      state.tick += 1;
      state.vy += 0.3;
      state.y += state.vy;
      state.pillars.forEach((pillar) => {
        pillar.x -= game.demo.config.speed;
        if (!pillar.passed && pillar.x < 120) {
          pillar.passed = true;
          state.gates += 1;
          setGates(state.gates);
        }
      });
      state.pillars = state.pillars.filter((pillar) => pillar.x > -75);
      if (state.pillars.length < 2)
        state.pillars.push({
          x: 700,
          gap: 110 + ((state.tick * 13) % 130),
          passed: false,
        });
      const crashed =
        state.y < 10 ||
        state.y > 338 ||
        state.pillars.some(
          (pillar) =>
            pillar.x < 150 &&
            pillar.x + 60 > 92 &&
            (state.y < pillar.gap - 58 || state.y > pillar.gap + 58)
        );
      if (crashed) {
        setStatus("lost");
        finish("lost", state.gates * 120);
        return;
      }
      if (state.gates >= goal) {
        setStatus("won");
        finish("won", state.gates * 150);
        return;
      }
      const sky = context.createLinearGradient(0, 0, 0, 360);
      sky.addColorStop(0, "#dff6ff");
      sky.addColorStop(1, "#e6f2d4");
      context.fillStyle = sky;
      context.fillRect(0, 0, 640, 360);
      context.fillStyle = "rgba(255,255,255,.9)";
      for (let i = 0; i < 5; i += 1) {
        context.beginPath();
        context.ellipse(
          70 + i * 145,
          55 + (i % 2) * 45,
          55,
          22,
          0,
          0,
          Math.PI * 2
        );
        context.fill();
      }
      context.fillStyle = "#5c7938";
      state.pillars.forEach((pillar) => {
        context.fillRect(pillar.x, 0, 60, pillar.gap - 58);
        context.fillRect(pillar.x, pillar.gap + 58, 60, 360);
        context.fillStyle = "#34502c";
        context.fillRect(pillar.x - 6, pillar.gap - 72, 72, 14);
        context.fillRect(pillar.x - 6, pillar.gap + 58, 72, 14);
        context.fillStyle = "#5c7938";
      });
      context.save();
      context.translate(120, state.y);
      context.rotate(Math.max(-0.35, Math.min(0.55, state.vy * 0.05)));
      context.fillStyle = "#f59e0b";
      context.beginPath();
      context.moveTo(-28, 0);
      context.lineTo(24, -13);
      context.lineTo(18, 13);
      context.closePath();
      context.fill();
      context.fillStyle = "#fff";
      context.fillRect(-5, -4, 23, 8);
      context.restore();
      frame.current = requestAnimationFrame(tick);
    };
    frame.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame.current);
  }, [finish, game.demo.config.speed, goal, paused, status]);
  return (
    <div className="play-surface canvas-game flyer-game" onPointerDown={flap}>
      <div className="game-hud">
        <GameStatus label="Gates" value={`${gates}/${goal}`} />
        <GameStatus label="Control" value="Tap" />
        <PauseButton
          paused={paused}
          disabled={status !== "playing"}
          setPaused={setPaused}
        />
      </div>
      <canvas ref={canvasRef} aria-label="Cave flyer play field" />
      {status === "ready" && (
        <GameOverlay
          title="Thread the cave"
          copy={`Tap, click or press Space to fly through ${goal} gaps.`}
          action="Launch glider"
          onAction={setup}
        />
      )}
      {paused && status === "playing" && (
        <GameOverlay
          title="Paused"
          copy="The glider is holding position."
          action="Continue"
          onAction={() => setPaused(false)}
        />
      )}
      {(status === "won" || status === "lost") && (
        <GameOverlay
          title={status === "won" ? "Course cleared!" : "Glider down"}
          copy={`${gates} gates passed.`}
          action="Fly again"
          onAction={setup}
        />
      )}
    </div>
  );
}

export function SlingshotGame({ unlocked, onFinish }) {
  const canvasRef = useRef(null);
  const frame = useRef(null);
  const model = useRef(null);
  const drag = useRef(null);
  const [status, setStatus] = useState("ready");
  const [paused, setPaused] = useState(false);
  const [height, setHeight] = useState(0);
  const finish = useFinish(onFinish);
  const goal = unlocked ? 8 : 4;
  const setup = useCallback(() => {
    model.current = {
      x: 320,
      y: 323,
      vx: 0,
      vy: 0,
      resting: true,
      platform: 0,
      platforms: [
        { x: 250, y: 345, w: 140 },
        { x: 390, y: 270, w: 125 },
        { x: 135, y: 200, w: 130 },
        { x: 350, y: 125, w: 125 },
        { x: 185, y: 55, w: 120 },
      ],
      tick: 0,
    };
    drag.current = null;
    setHeight(0);
    setPaused(false);
    setStatus("playing");
  }, []);
  usePauseOnBlur(setPaused);
  const point = (event) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return {
      x: ((event.clientX - rect.left) * 640) / rect.width,
      y: ((event.clientY - rect.top) * 400) / rect.height,
    };
  };
  const down = (event) => {
    if (status !== "playing" || paused || !model.current?.resting) return;
    drag.current = point(event);
    canvasRef.current.setPointerCapture?.(event.pointerId);
  };
  const up = (event) => {
    if (!drag.current || !model.current) return;
    const end = point(event);
    const state = model.current;
    state.vx = Math.max(-8, Math.min(8, (drag.current.x - end.x) * 0.08));
    state.vy = Math.max(-11, Math.min(-3, (drag.current.y - end.y) * 0.08));
    state.resting = false;
    drag.current = null;
  };
  useEffect(() => {
    if (status !== "playing" || paused) return undefined;
    const context = setCanvasSize(canvasRef.current, 640, 400);
    const tick = () => {
      const state = model.current;
      state.tick += 1;
      const previousY = state.y;
      if (!state.resting) {
        state.vy += 0.34;
        state.x += state.vx;
        state.y += state.vy;
        if (state.x < 0) state.x = 640;
        if (state.x > 640) state.x = 0;
        const landing = state.platforms.findIndex(
          (platform, index) =>
            index > state.platform &&
            state.vy > 0 &&
            previousY + 16 <= platform.y &&
            state.y + 16 >= platform.y &&
            state.x > platform.x &&
            state.x < platform.x + platform.w
        );
        if (landing >= 0) {
          state.platform = landing;
          state.y = state.platforms[landing].y - 16;
          state.vx = 0;
          state.vy = 0;
          state.resting = true;
          setHeight(landing);
          if (landing >= goal) {
            setStatus("won");
            finish("won", landing * 250);
            return;
          }
        }
        if (state.y > 430) {
          setStatus("lost");
          finish("lost", state.platform * 150);
          return;
        }
      }
      const sky = context.createLinearGradient(0, 0, 0, 400);
      sky.addColorStop(0, "#08184f");
      sky.addColorStop(1, "#6955c9");
      context.fillStyle = sky;
      context.fillRect(0, 0, 640, 400);
      context.fillStyle = "#fff";
      for (let i = 0; i < 35; i += 1)
        context.fillRect((i * 79) % 640, (i * 47) % 260, 2, 2);
      state.platforms.forEach((platform, index) => {
        context.fillStyle = index === state.platform ? "#facc15" : "#45a049";
        context.beginPath();
        context.ellipse(
          platform.x + platform.w / 2,
          platform.y,
          platform.w / 2,
          16,
          0,
          0,
          Math.PI * 2
        );
        context.fill();
        context.fillStyle = "#6b4b2a";
        context.fillRect(platform.x + 20, platform.y, platform.w - 40, 14);
      });
      if (drag.current) {
        context.strokeStyle = "rgba(255,255,255,.7)";
        context.setLineDash([5, 7]);
        context.beginPath();
        context.moveTo(state.x, state.y);
        context.lineTo(drag.current.x, drag.current.y);
        context.stroke();
        context.setLineDash([]);
      }
      context.fillStyle = "#f472b6";
      context.beginPath();
      context.arc(state.x, state.y, 16, 0, Math.PI * 2);
      context.fill();
      context.fillStyle = "white";
      context.beginPath();
      context.moveTo(state.x - 14, state.y);
      context.lineTo(state.x - 30, state.y - 8);
      context.lineTo(state.x - 25, state.y + 8);
      context.fill();
      frame.current = requestAnimationFrame(tick);
    };
    frame.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame.current);
  }, [finish, goal, paused, status]);
  return (
    <div className="play-surface canvas-game slingshot-game">
      <div className="game-hud">
        <GameStatus label="Height" value={`${height}/${goal}`} />
        <GameStatus label="Aim" value="Pull + release" />
        <PauseButton
          paused={paused}
          disabled={status !== "playing"}
          setPaused={setPaused}
        />
      </div>
      <canvas
        ref={canvasRef}
        aria-label="Slingshot climber play field"
        onPointerDown={down}
        onPointerUp={up}
      />
      {status === "ready" && (
        <GameOverlay
          title="Climb by slingshot"
          copy="Drag down from the winged hero, aim at the next floating island, then release."
          action="Enter tower"
          onAction={setup}
        />
      )}
      {paused && status === "playing" && (
        <GameOverlay
          title="Paused"
          copy="Your launch is held."
          action="Continue"
          onAction={() => setPaused(false)}
        />
      )}
      {(status === "won" || status === "lost") && (
        <GameOverlay
          title={status === "won" ? "Tower topped!" : "You missed the islands"}
          copy={`Height: ${height}`}
          action="Climb again"
          onAction={setup}
        />
      )}
    </div>
  );
}

export function SliceGame({ unlocked, onFinish }) {
  const canvasRef = useRef(null);
  const frame = useRef(null);
  const model = useRef(null);
  const pointer = useRef({ x: -100, y: -100, down: false });
  const [status, setStatus] = useState("ready");
  const [paused, setPaused] = useState(false);
  const [score, setScore] = useState(0);
  const [lives, setLives] = useState(3);
  const finish = useFinish(onFinish);
  const goal = unlocked ? 30 : 12;
  const setup = useCallback(() => {
    model.current = { items: [], tick: 0, score: 0, lives: 3 };
    pointer.current = { x: -100, y: -100, down: false };
    setScore(0);
    setLives(3);
    setPaused(false);
    setStatus("playing");
  }, []);
  usePauseOnBlur(setPaused);
  const movePointer = (event) => {
    const rect = canvasRef.current.getBoundingClientRect();
    pointer.current.x = ((event.clientX - rect.left) * 640) / rect.width;
    pointer.current.y = ((event.clientY - rect.top) * 400) / rect.height;
    pointer.current.down = true;
  };
  useEffect(() => {
    if (status !== "playing" || paused) return undefined;
    const context = setCanvasSize(canvasRef.current, 640, 400);
    const colors = ["#ef4444", "#84cc16", "#f59e0b", "#a855f7"];
    const tick = () => {
      const state = model.current;
      state.tick += 1;
      if (state.tick % 42 === 0)
        state.items.push({
          x: 80 + ((state.tick * 47) % 480),
          y: 420,
          vx: -1.8 + (state.tick % 4) * 1.2,
          vy: -9 - (state.tick % 3),
          r: 19,
          bomb: state.tick % 252 === 0,
          sliced: false,
        });
      state.items.forEach((item) => {
        item.x += item.vx;
        item.y += item.vy;
        item.vy += 0.2;
        if (
          pointer.current.down &&
          !item.sliced &&
          Math.hypot(item.x - pointer.current.x, item.y - pointer.current.y) <
            item.r + 12
        ) {
          item.sliced = true;
          if (item.bomb) {
            state.lives -= 1;
            setLives(state.lives);
          } else {
            state.score += 1;
            setScore(state.score);
          }
        }
      });
      state.items = state.items.filter((item) => item.y < 445 && !item.sliced);
      pointer.current.down = false;
      if (state.lives <= 0) {
        setStatus("lost");
        finish("lost", state.score * 100);
        return;
      }
      if (state.score >= goal) {
        setStatus("won");
        finish("won", state.score * 120);
        return;
      }
      const bg = context.createRadialGradient(320, 180, 30, 320, 200, 430);
      bg.addColorStop(0, "#401447");
      bg.addColorStop(1, "#071326");
      context.fillStyle = bg;
      context.fillRect(0, 0, 640, 400);
      context.strokeStyle = "rgba(255,255,255,.05)";
      for (let i = 0; i < 10; i += 1) {
        context.beginPath();
        context.moveTo(0, i * 45);
        context.lineTo(640, i * 45 - 70);
        context.stroke();
      }
      state.items.forEach((item, index) => {
        context.fillStyle = item.bomb
          ? "#171717"
          : colors[index % colors.length];
        context.beginPath();
        context.arc(item.x, item.y, item.r, 0, Math.PI * 2);
        context.fill();
        context.fillStyle = item.bomb ? "#ef4444" : "rgba(255,255,255,.45)";
        context.fillRect(item.x - 3, item.y - item.r - 8, 6, 10);
      });
      context.strokeStyle = "#f8fafc";
      context.lineWidth = 4;
      context.beginPath();
      context.moveTo(pointer.current.x - 22, pointer.current.y + 12);
      context.lineTo(pointer.current.x + 22, pointer.current.y - 12);
      context.stroke();
      frame.current = requestAnimationFrame(tick);
    };
    frame.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame.current);
  }, [finish, goal, paused, status]);
  return (
    <div className="play-surface canvas-game slice-game">
      <div className="game-hud">
        <GameStatus label="Fruit" value={`${score}/${goal}`} />
        <GameStatus label="Lives" value={lives} />
        <PauseButton
          paused={paused}
          disabled={status !== "playing"}
          setPaused={setPaused}
        />
      </div>
      <canvas
        ref={canvasRef}
        aria-label="Fruit slicing play field"
        onPointerDown={movePointer}
        onPointerMove={(event) => {
          if (event.buttons || event.pointerType === "touch")
            movePointer(event);
        }}
      />
      {status === "ready" && (
        <GameOverlay
          title="Slice fruit, spare bombs"
          copy={`Drag across ${goal} fruit. Three bomb strikes end the run.`}
          action="Draw blade"
          onAction={setup}
        />
      )}{" "}
      {paused && status === "playing" && (
        <GameOverlay
          title="Paused"
          copy="The fruit is frozen."
          action="Continue"
          onAction={() => setPaused(false)}
        />
      )}{" "}
      {(status === "won" || status === "lost") && (
        <GameOverlay
          title={
            status === "won" ? "Perfect harvest!" : "Blade hit three bombs"
          }
          copy={`${score} fruit sliced.`}
          action="Slice again"
          onAction={setup}
        />
      )}
    </div>
  );
}

export function ShooterGame({ unlocked, onFinish }) {
  const canvasRef = useRef(null);
  const frame = useRef(null);
  const model = useRef(null);
  const keys = useRef({});
  const [status, setStatus] = useState("ready");
  const [paused, setPaused] = useState(false);
  const [kills, setKills] = useState(0);
  const [lives, setLives] = useState(3);
  const finish = useFinish(onFinish);
  const goal = unlocked ? 28 : 10;
  const setup = useCallback(() => {
    model.current = {
      player: { x: 320, y: 210 },
      enemies: [],
      bullets: [],
      tick: 0,
      kills: 0,
      lives: 3,
    };
    setKills(0);
    setLives(3);
    setPaused(false);
    setStatus("playing");
  }, []);
  usePauseOnBlur(setPaused);
  useEffect(() => {
    const down = (event) => {
      keys.current[event.key.toLowerCase()] = true;
      if (
        [
          "arrowup",
          "arrowdown",
          "arrowleft",
          "arrowright",
          "w",
          "a",
          "s",
          "d",
          "p",
        ].includes(event.key.toLowerCase())
      )
        event.preventDefault();
      if (event.key.toLowerCase() === "p") setPaused((value) => !value);
    };
    const up = (event) => {
      keys.current[event.key.toLowerCase()] = false;
    };
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup", up);
    };
  }, []);
  const point = (event) => {
    if (status !== "playing" || paused) return;
    const rect = canvasRef.current.getBoundingClientRect();
    model.current.player.x = ((event.clientX - rect.left) * 640) / rect.width;
    model.current.player.y = ((event.clientY - rect.top) * 400) / rect.height;
  };
  useEffect(() => {
    if (status !== "playing" || paused) return undefined;
    const context = setCanvasSize(canvasRef.current, 640, 400);
    const tick = () => {
      const state = model.current;
      state.tick += 1;
      const player = state.player;
      const speed = 3.8;
      if (keys.current.w || keys.current.arrowup) player.y -= speed;
      if (keys.current.s || keys.current.arrowdown) player.y += speed;
      if (keys.current.a || keys.current.arrowleft) player.x -= speed;
      if (keys.current.d || keys.current.arrowright) player.x += speed;
      player.x = Math.max(18, Math.min(622, player.x));
      player.y = Math.max(70, Math.min(382, player.y));
      if (state.tick % 55 === 0) {
        const side = state.tick % 4;
        state.enemies.push({
          x: side === 0 ? 5 : side === 2 ? 635 : 80 + ((state.tick * 31) % 480),
          y:
            side === 1 ? 65 : side === 3 ? 395 : 80 + ((state.tick * 17) % 270),
          hp: 1,
        });
      }
      const nearest = [...state.enemies].sort(
        (a, b) =>
          Math.hypot(a.x - player.x, a.y - player.y) -
          Math.hypot(b.x - player.x, b.y - player.y)
      )[0];
      if (nearest && state.tick % 22 === 0) {
        const distance =
          Math.hypot(nearest.x - player.x, nearest.y - player.y) || 1;
        state.bullets.push({
          x: player.x,
          y: player.y,
          vx: ((nearest.x - player.x) / distance) * 6,
          vy: ((nearest.y - player.y) / distance) * 6,
        });
      }
      state.enemies.forEach((enemy) => {
        const distance =
          Math.hypot(player.x - enemy.x, player.y - enemy.y) || 1;
        enemy.x += ((player.x - enemy.x) / distance) * 1.15;
        enemy.y += ((player.y - enemy.y) / distance) * 1.15;
      });
      state.bullets.forEach((bullet) => {
        bullet.x += bullet.vx;
        bullet.y += bullet.vy;
      });
      state.bullets.forEach((bullet) =>
        state.enemies.forEach((enemy) => {
          if (
            enemy.hp > 0 &&
            Math.hypot(bullet.x - enemy.x, bullet.y - enemy.y) < 17
          ) {
            enemy.hp = 0;
            bullet.dead = true;
            state.kills += 1;
            setKills(state.kills);
          }
        })
      );
      state.enemies.forEach((enemy) => {
        if (
          enemy.hp > 0 &&
          Math.hypot(player.x - enemy.x, player.y - enemy.y) < 24
        ) {
          enemy.hp = 0;
          state.lives -= 1;
          setLives(state.lives);
        }
      });
      state.enemies = state.enemies.filter((enemy) => enemy.hp > 0);
      state.bullets = state.bullets.filter(
        (bullet) =>
          !bullet.dead &&
          bullet.x > 0 &&
          bullet.x < 640 &&
          bullet.y > 0 &&
          bullet.y < 400
      );
      if (state.lives <= 0) {
        setStatus("lost");
        finish("lost", state.kills * 120);
        return;
      }
      if (state.kills >= goal) {
        setStatus("won");
        finish("won", state.kills * 150 + state.lives * 100);
        return;
      }
      context.fillStyle = "#10131d";
      context.fillRect(0, 0, 640, 400);
      context.strokeStyle = "#252a39";
      for (let x = 0; x < 640; x += 40) {
        context.beginPath();
        context.moveTo(x, 0);
        context.lineTo(x, 400);
        context.stroke();
      }
      for (let y = 0; y < 400; y += 40) {
        context.beginPath();
        context.moveTo(0, y);
        context.lineTo(640, y);
        context.stroke();
      }
      state.enemies.forEach((enemy) => {
        context.fillStyle = "#9fbe54";
        context.beginPath();
        context.arc(enemy.x, enemy.y, 15, 0, Math.PI * 2);
        context.fill();
        context.fillStyle = "#d9f99d";
        context.fillRect(enemy.x - 7, enemy.y - 4, 4, 4);
        context.fillRect(enemy.x + 3, enemy.y - 4, 4, 4);
      });
      context.fillStyle = "#fb7185";
      state.bullets.forEach((bullet) => {
        context.beginPath();
        context.arc(bullet.x, bullet.y, 4, 0, Math.PI * 2);
        context.fill();
      });
      context.fillStyle = "#38bdf8";
      context.beginPath();
      context.arc(player.x, player.y, 17, 0, Math.PI * 2);
      context.fill();
      context.strokeStyle = "white";
      context.lineWidth = 4;
      context.beginPath();
      context.moveTo(player.x, player.y);
      if (nearest)
        context.lineTo(
          player.x + (nearest.x - player.x) * 0.13,
          player.y + (nearest.y - player.y) * 0.13
        );
      else context.lineTo(player.x + 26, player.y);
      context.stroke();
      frame.current = requestAnimationFrame(tick);
    };
    frame.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame.current);
  }, [finish, goal, paused, status]);
  return (
    <div className="play-surface canvas-game shooter-game">
      <div className="game-hud">
        <GameStatus label="Wave kills" value={`${kills}/${goal}`} />
        <GameStatus label="Health" value={lives} />
        <PauseButton
          paused={paused}
          disabled={status !== "playing"}
          setPaused={setPaused}
        />
      </div>
      <canvas
        ref={canvasRef}
        aria-label="Top-down survival shooter"
        onPointerDown={point}
        onPointerMove={(event) => {
          if (event.buttons || event.pointerType === "touch") point(event);
        }}
      />
      <p className="game-hint">
        Move with WASD / arrows or drag. Your blaster targets the nearest
        creature.
      </p>
      {status === "ready" && (
        <GameOverlay
          title="Survive the night wave"
          copy={`Keep moving while auto-fire defeats ${goal} creatures.`}
          action="Start wave"
          onAction={setup}
        />
      )}{" "}
      {paused && status === "playing" && (
        <GameOverlay
          title="Paused"
          copy="The wave is held."
          action="Continue"
          onAction={() => setPaused(false)}
        />
      )}{" "}
      {(status === "won" || status === "lost") && (
        <GameOverlay
          title={status === "won" ? "Wave survived!" : "The swarm closed in"}
          copy={`${kills} creatures defeated.`}
          action="Try again"
          onAction={setup}
        />
      )}
    </div>
  );
}

export function SpaceGame({ unlocked, onFinish }) {
  const canvasRef = useRef(null);
  const frame = useRef(null);
  const model = useRef(null);
  const keys = useRef({});
  const [status, setStatus] = useState("ready");
  const [paused, setPaused] = useState(false);
  const [stars, setStars] = useState(0);
  const [lives, setLives] = useState(3);
  const finish = useFinish(onFinish);
  const goal = unlocked ? 18 : 7;
  const setup = useCallback(() => {
    model.current = { x: 320, objects: [], tick: 0, stars: 0, lives: 3 };
    setStars(0);
    setLives(3);
    setPaused(false);
    setStatus("playing");
  }, []);
  usePauseOnBlur(setPaused);
  useEffect(() => {
    const down = (event) => {
      keys.current[event.key.toLowerCase()] = true;
      if (
        ["arrowleft", "arrowright", "a", "d", "p"].includes(
          event.key.toLowerCase()
        )
      )
        event.preventDefault();
      if (event.key.toLowerCase() === "p") setPaused((value) => !value);
    };
    const up = (event) => {
      keys.current[event.key.toLowerCase()] = false;
    };
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup", up);
    };
  }, []);
  const point = (event) => {
    if (status !== "playing" || paused) return;
    const rect = canvasRef.current.getBoundingClientRect();
    model.current.x = Math.max(
      24,
      Math.min(616, ((event.clientX - rect.left) * 640) / rect.width)
    );
  };
  useEffect(() => {
    if (status !== "playing" || paused) return undefined;
    const context = setCanvasSize(canvasRef.current, 640, 400);
    const tick = () => {
      const state = model.current;
      state.tick += 1;
      if (keys.current.a || keys.current.arrowleft) state.x -= 5;
      if (keys.current.d || keys.current.arrowright) state.x += 5;
      state.x = Math.max(24, Math.min(616, state.x));
      if (state.tick % 34 === 0)
        state.objects.push({
          x: 30 + ((state.tick * 83) % 580),
          y: -20,
          vy: 2.7 + (state.tick % 5) * 0.25,
          star: state.tick % 102 === 0,
          spin: 0,
        });
      state.objects.forEach((object) => {
        object.y += object.vy;
        object.spin += 0.05;
        if (
          object.y > 345 &&
          object.y < 390 &&
          Math.abs(object.x - state.x) < 25 &&
          !object.hit
        ) {
          object.hit = true;
          if (object.star) {
            state.stars += 1;
            setStars(state.stars);
          } else {
            state.lives -= 1;
            setLives(state.lives);
          }
        }
      });
      state.objects = state.objects.filter(
        (object) => object.y < 430 && !object.hit
      );
      if (state.lives <= 0) {
        setStatus("lost");
        finish("lost", state.stars * 180);
        return;
      }
      if (state.stars >= goal) {
        setStatus("won");
        finish("won", state.stars * 220 + state.lives * 100);
        return;
      }
      const bg = context.createLinearGradient(0, 0, 0, 400);
      bg.addColorStop(0, "#080b2c");
      bg.addColorStop(1, "#401465");
      context.fillStyle = bg;
      context.fillRect(0, 0, 640, 400);
      context.fillStyle = "white";
      for (let i = 0; i < 60; i += 1) {
        context.globalAlpha = 0.35 + (i % 4) * 0.15;
        context.fillRect((i * 97) % 640, (i * 53 + state.tick) % 430, 2, 2);
      }
      context.globalAlpha = 1;
      state.objects.forEach((object) => {
        context.save();
        context.translate(object.x, object.y);
        context.rotate(object.spin);
        context.fillStyle = object.star ? "#facc15" : "#8b8ca7";
        if (object.star) {
          context.beginPath();
          for (let i = 0; i < 10; i += 1) {
            const radius = i % 2 ? 6 : 15;
            const angle = -Math.PI / 2 + (i * Math.PI) / 5;
            context.lineTo(Math.cos(angle) * radius, Math.sin(angle) * radius);
          }
          context.closePath();
          context.fill();
        } else {
          context.beginPath();
          context.moveTo(-18, -10);
          context.lineTo(7, -17);
          context.lineTo(20, 5);
          context.lineTo(0, 17);
          context.lineTo(-20, 7);
          context.closePath();
          context.fill();
        }
        context.restore();
      });
      context.save();
      context.translate(state.x, 355);
      context.fillStyle = "#22d3ee";
      context.beginPath();
      context.moveTo(0, -25);
      context.lineTo(20, 19);
      context.lineTo(0, 12);
      context.lineTo(-20, 19);
      context.closePath();
      context.fill();
      context.fillStyle = "#fb7185";
      context.fillRect(-5, 13, 10, 18);
      context.restore();
      frame.current = requestAnimationFrame(tick);
    };
    frame.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame.current);
  }, [finish, goal, paused, status]);
  return (
    <div className="play-surface canvas-game space-game">
      <div className="game-hud">
        <GameStatus label="Star cores" value={`${stars}/${goal}`} />
        <GameStatus label="Hull" value={lives} />
        <PauseButton
          paused={paused}
          disabled={status !== "playing"}
          setPaused={setPaused}
        />
      </div>
      <canvas
        ref={canvasRef}
        aria-label="Nebula survival play field"
        onPointerDown={point}
        onPointerMove={(event) => {
          if (event.buttons || event.pointerType === "touch") point(event);
        }}
      />
      <p className="game-hint">
        Move left and right. Collect gold star cores; avoid grey debris.
      </p>
      {status === "ready" && (
        <GameOverlay
          title="Shift through the nebula"
          copy={`Collect ${goal} star cores without losing the hull.`}
          action="Launch ship"
          onAction={setup}
        />
      )}{" "}
      {paused && status === "playing" && (
        <GameOverlay
          title="Paused"
          copy="Your ship is holding course."
          action="Continue"
          onAction={() => setPaused(false)}
        />
      )}{" "}
      {(status === "won" || status === "lost") && (
        <GameOverlay
          title={status === "won" ? "Nebula crossed!" : "Hull breached"}
          copy={`${stars} star cores collected.`}
          action="Shift again"
          onAction={setup}
        />
      )}
    </div>
  );
}
